import { Link } from '@tanstack/react-router'

import { ArrowForwardIcon } from '@/components/icon'
import {
  Badge,
  Box,
  HStack,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@/components/ui'
import { TRIAL_PROGRESS_LABELS } from '@/constants/trialDetailLabels'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'

const relatedTrialItemFragment = graphql(`
  fragment RelatedTrialFragment on Trial {
    isNew
    trialNumber
    latestDetail {
      plainTrialName
      targetDiseaseName
      progress
    }
  }
`)
type RelatedTrialItemProps = {
  item: FragmentType<typeof relatedTrialItemFragment>
}

const RelatedTrialItem = ({ item }: RelatedTrialItemProps) => {
  const {
    isNew,
    trialNumber,
    latestDetail: { plainTrialName, targetDiseaseName, progress },
  } = getFragmentData(relatedTrialItemFragment, item)
  return (
    <LinkBox
      as={HStack}
      w="full"
      minW="200px"
      px={4}
      py={3}
      gap={4}
      bgColor="orange.100"
      borderRadius="lg"
      boxShadow="sm"
      _hover={{ bgColor: 'orange.200' }}
    >
      <Stack flexGrow={1} gap={0} w={0} minW={0}>
        <HStack gap={4} rowGap={1} wrap="wrap">
          <HStack gap={2}>
            <Text fontSize="sm" wordBreak="keep-all">
              試験ID
            </Text>
            <Text fontSize="sm">:</Text>
            <Text as="span" fontWeight="semibold" wordBreak="keep-all">
              <LinkOverlay asChild>
                <Link
                  to="/trials/$trialNumber"
                  target="_blank"
                  rel="noopener noreferrer"
                  params={{ trialNumber }}
                >
                  {trialNumber}
                </Link>
              </LinkOverlay>
            </Text>
          </HStack>
          {(isNew || progress) && (
            <HStack gap={2} wrap="wrap">
              {isNew && (
                <Badge colorPalette="red" variant="outline">
                  新規
                </Badge>
              )}
              {progress && (
                <Badge colorPalette="gray" variant="outline">
                  {TRIAL_PROGRESS_LABELS[progress]}
                </Badge>
              )}
            </HStack>
          )}
        </HStack>

        <Text
          fontSize="sm"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {plainTrialName}
        </Text>

        <Text fontSize="sm" fontWeight="semibold">
          {targetDiseaseName}
        </Text>
      </Stack>

      <Box flexShrink={0}>
        <ArrowForwardIcon color="var(--chakra-colors-gray-500)" />
      </Box>
    </LinkBox>
  )
}

const relatedTrialsFragment = graphql(`
  fragment RelatedTrialsTrial on Trial {
    trialUid
    ...RelatedTrialFragment
  }
`)

type RelatedTrialListProps = {
  relatedTrials: FragmentType<typeof relatedTrialsFragment>[]
}

export const RelatedTrialList = ({ relatedTrials }: RelatedTrialListProps) => {
  const relatedTrialsData = getFragmentData(
    relatedTrialsFragment,
    relatedTrials,
  )
  return (
    <Stack h="full" gap={0} bgColor="white">
      <Box px={4} py={3} top={0} bgColor="white">
        <HStack gap={0}>
          <Text color="text.sub" fontWeight="semibold">
            関連する試験
          </Text>
          <Text color="text.sub" fontSize="sm">
            （同じ病種、病期、治験責任医師）
          </Text>
        </HStack>
      </Box>

      <Stack px={2} pb={4} gap={1} overflow="auto">
        {relatedTrialsData.map(item => (
          <RelatedTrialItem key={item.trialUid} item={item} />
        ))}
      </Stack>
    </Stack>
  )
}
